/**
 * Client – My Coach: view active coach, stats, end-coaching action.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  getActiveCoachRelationship,
  getCoach,
  endCoaching,
} from '../../domain/usecases/coaching';
import { programAssignmentRepo } from '../../data/repositories/ProgramAssignmentRepository';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Button from '../../shared/ui/Button.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import styles from './MyCoachPage.module.css';

function formatDate(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

export function MyCoachPage() {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rel, setRel] = useState(null);      // coachClient doc
  const [coach, setCoach] = useState(null);
  const [programCount, setProgramCount] = useState(null);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function load() {
      const activRel = await getActiveCoachRelationship(userId);
      if (cancelled) return;
      if (!activRel) { setLoading(false); return; }
      setRel(activRel);

      const [coachDoc, assignments] = await Promise.all([
        getCoach(activRel.coachId),
        programAssignmentRepo.listByClient(userId).catch(() => null),
      ]);
      if (cancelled) return;
      setCoach(coachDoc || null);
      if (assignments) setProgramCount(assignments.length);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId]);

  async function handleEndCoaching() {
    if (!rel || !coach) return;
    const confirmed = window.confirm(
      `End coaching with ${coach.name || 'your coach'}? This cannot be undone.`
    );
    if (!confirmed) return;
    setEnding(true);
    try {
      await endCoaching(rel.id);
      navigate('/coaching');
    } finally {
      setEnding(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Button variant="ghost" as={Link} to="/coaching" style={{ marginBottom: '1rem' }}>
          ← Coaching
        </Button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} width="100%" height={80} radius="var(--radius-md)" />
          ))}
        </div>
      </div>
    );
  }

  if (!rel) {
    return (
      <div className={styles.page}>
        <Button variant="ghost" as={Link} to="/coaching" style={{ marginBottom: '1rem' }}>
          ← Coaching
        </Button>
        <EmptyState
          icon="coach"
          title="No active coach"
          body="Find a coach to get started."
          action={
            <Button variant="primary" as={Link} to="/coaching">
              Find a coach
            </Button>
          }
        />
      </div>
    );
  }

  const isSuspended =
    coach && (coach.isActiveCoach === false || coach.approvalStatus !== 'approved');

  return (
    <div className={styles.page}>
      <Button variant="ghost" as={Link} to="/coaching" style={{ marginBottom: '1rem' }}>
        ← Coaching
      </Button>

      {/* Header */}
      <div className={styles.header}>
        <Avatar
          src={coach?.profileImage}
          initials={(coach?.name || 'C').charAt(0)}
          size="lg"
          verified={coach?.isVerified}
          alt={coach?.name || 'Coach'}
        />
        <Display as="h1" size="l">{coach?.name || 'Your Coach'}</Display>
        {coach?.specialization && (
          <Body tone="secondary" style={{ textTransform: 'capitalize' }}>
            {coach.specialization}
          </Body>
        )}
      </div>

      {/* Suspended banner */}
      {isSuspended && (
        <div className={styles.suspendedBanner}>
          <Card frame="gold">
            <Body>
              Your coach is no longer active. You can continue viewing past programs, but new
              assignments are paused.
            </Body>
          </Card>
        </div>
      )}

      {/* Stats */}
      <div className={styles.stats}>
        <Stat label="SINCE" value={formatDate(rel.startDate)} />
        {programCount !== null && <Stat label="PROGRAMS" value={programCount} />}
      </div>

      {/* Navigation */}
      <div className={styles.navButtons}>
        <Button variant="secondary" size="lg" as={Link} to="/coaching/my-plan">
          View my plan
        </Button>
        <Button variant="secondary" size="lg" as={Link} to="/coaching/my-programs">
          View my programs
        </Button>
      </div>

      {/* End coaching */}
      <div className={styles.danger}>
        <Button variant="danger" onClick={handleEndCoaching} disabled={ending}>
          {ending ? 'Ending…' : 'End coaching'}
        </Button>
      </div>
    </div>
  );
}
